import React from "react";
import { dollarFormatter } from "../dollarFormatter";
import dayjs from "dayjs";

export const getTextSnippet = (name, data) => {
  if (!data) {
    return "";
  }
  switch (name) {
    case "homeBuyerType":
      return getHomeBuyerTypeSnippet(data);
    case "closingDate90":
      return getClosingDateSnippet(data);
    case "closingDateAway":
      return getClosingDateAwaySnippet(data);
    default:
      return getTextSnippetFromData(name, data);
  }
};

const getTextSnippetFromData = (name, data) => {
  const savedData = data[name];
  switch (name) {
    case "price":
    case "downPayment":
    case "existingLoanBalance":
    case "amountOfCashOut":
      return dollarFormatter(savedData);
    case "hasWaivedEscrow":
      return getHasWaivedEscrowSnippet(savedData);
    case "isVeteran":
      return savedData ? "served" : "not served";
    case "isSelfEmployed":
      return savedData ? "self-employed" : "not self-employed";
    case "isUsCitizen":
      return savedData ? "a U.S. citizen" : "not a U.S. citizen";
    case "hasRealEstateAgent":
      return getHasRealEstateAgentSnippet(savedData);
    case "forSaleByOwner":
      return getForSaleByOwnerSnippet(savedData);
    case "isBuyingAsIs":
      return getIsBuyingAsIsSnippet(savedData);
    case "hasTitleCompany":
      return getHasTitleCompanySnippet(savedData);
    case "takeCashOut":
      return savedData ? "would like to" : "would not like to";
    case "occupancyType":
      return getOccupancySnippet(savedData);
    case "propertyType":
      return getPropertyTypeSnippet(savedData);
    case "loanOptions":
      return getLoanOptionsSnippet(savedData);
    case "veteranType":
      return getVeteranTypeSnippet(savedData);
    case "disabledVeteran":
      return getDisabledVeteranSnippet(savedData);
    case "vaFirstTimeUse":
      return getVaFirstTimeUseSnippet(savedData);
    default:
      return String(savedData);
  }
};

const formatLoanSnippet = (snippet) => {
  if (snippet.includes("Fixed")) {
    const years = snippet.match(/\d+/)[0];
    return `${years}-year Fixed`;
  } else if (snippet.includes("ARM")) {
    const parts = snippet
      .match(/ARM(\d+)(\d+)/)
      .slice(1)
      .map((part) => parseInt(part, 10));
    const initial = parts[0];
    const fixed = parts[1];
    return `${initial}/${fixed} ARM`;
  }
};

const getLoanOptionsSnippet = (savedData) => {
  return savedData.map(
    (loan, index) =>
      `${formatLoanSnippet(loan)}${index !== savedData.length - 1 ? ", " : ""}`
  );
};

const getVeteranTypeSnippet = (savedData) =>
  savedData === "NationalGuardOrReserves"
    ? "National Guard or Reserves"
    : "Active Duty";

const getDisabledVeteranSnippet = (savedData) =>
  savedData ? "disabled" : "not disabled";

const getVaFirstTimeUseSnippet = (savedData) =>
  savedData ? "never had a VA loan" : "had a VA loan before";

const getPropertyTypeSnippet = (savedData) => {
  if (savedData === "Single") {
    return "single-family home";
  } else if (savedData === "Townhome") {
    return "townhouse";
  } else {
    return savedData.toLowerCase();
  }
};

const getOccupancySnippet = (savedData) => {
  if (savedData === "Rental") {
    return "rental property";
  } else if (savedData === "Secondary") {
    return "second home";
  } else {
    return "primary residence";
  }
};

const getHomeBuyerTypeSnippet = (data) => {
  const firstTimeBuyer = data.firstTimeBuyer;
  return firstTimeBuyer === "true" ? "not owned a home" : "owned a home";
};

const getClosingDateSnippet = (data) => {
  const closingDate = dayjs(data.plannedClosingDate);
  const daysUntilClosing = closingDate.diff(dayjs(), "day");
  if (daysUntilClosing > 90) {
    return "90 days or more";
  } else {
    return "30-90 days";
  }
};

const getClosingDateAwaySnippet = (data) => {
  const isWithinClosing = dayjs(data.plannedClosingDate).isBefore(
    dayjs().add(30, "day")
  );
  return isWithinClosing ? "less than 30 days away" : "at least 30 days away";
};

const getHasWaivedEscrowSnippet = (savedData) => {
  if (savedData === "Yes") {
    return "I will waive";
  } else if (savedData === "No") {
    return "I will not waive";
  } else {
    return "I am not sure if I will waive";
  }
};

const getHasRealEstateAgentSnippet = (savedData) => {
  if (savedData === "Yes") {
    return "working with";
  } else if (savedData === "No") {
    return "not working with";
  } else {
    return "not sure if I'm working with";
  }
};

const getForSaleByOwnerSnippet = (savedData) => {
  if (savedData === "Yes") {
    return "The property is";
  } else if (savedData === "No") {
    return "The property is not";
  } else {
    return "I'm not sure if the property is";
  }
};

const getIsBuyingAsIsSnippet = (savedData) => {
  if (savedData === "Yes") {
    return "am buying the home as-is";
  } else if (savedData === "No") {
    return "am not buying the home as-is";
  } else {
    return "am not sure if I'm buying the home as-is";
  }
};

const getHasTitleCompanySnippet = (savedData) => {
  if (savedData === "Yes") {
    return "I already have a title company";
  } else if (savedData === "No") {
    return "I do not have a title company";
  } else {
    return "I am not sure if I have a title company";
  }
};
